/* Expenses tab — flat editable table of monthly/annual overhead. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Save, Download } from "lucide-react";
import { api } from "@/lib/api";
import { downloadCsv } from "./reports";

const money = (n) => `$${(+n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const FREQ = ["Monthly", "Annual", "One-Time"];

// If a user pays annually or for a one-time cost, we still want the
// "Monthly Cost" column to reflect a true amortized monthly hit so the
// dashboard's opex sum is honest. This helper spreads the entered value
// into a per-month figure so ~roll-ups don't lie.
function toMonthly(row) {
  if (row.billing_frequency === "Annual") return row.monthly_cost / 12;
  if (row.billing_frequency === "One-Time") return row.monthly_cost / 12; // 1-yr amortization
  return row.monthly_cost;
}

export default function ExpensesTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/command-center/expenses");
      setRows(data.items || []);
    } catch { toast.error("Could not load expenses."); }
  };
  useEffect(() => { load(); }, []);

  const patch = (id, f, v) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, [f]: v, _dirty: true } : r));

  const save = async (r) => {
    setBusy(true);
    try {
      await api.patch(`/command-center/expenses/${r.id}`, {
        name: r.name, category: r.category,
        billing_frequency: r.billing_frequency,
        monthly_cost: +r.monthly_cost,
      });
      setRows((prev) => prev.map((x) => x.id === r.id ? { ...x, _dirty: false } : x));
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const addRow = async () => {
    try {
      const { data } = await api.post("/command-center/expenses", {
        name: "New Expense", category: "Software", billing_frequency: "Monthly", monthly_cost: 0,
      });
      setRows((prev) => [...prev, data]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Add failed."); }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/command-center/expenses/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch { toast.error("Delete failed."); }
  };

  const totalMonthly = rows.reduce((s, r) => s + toMonthly({ ...r, monthly_cost: +r.monthly_cost || 0 }), 0);
  const totalAnnual = totalMonthly * 12;

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="expenses-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--line)]">
              <th className="p-2 min-w-[160px]">Expense</th>
              <th className="p-2">Category</th>
              <th className="p-2">Billing</th>
              <th className="p-2">Cost ($)</th>
              <th className="p-2">Amortized / mo</th>
              <th className="p-2">Annual</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-[var(--ink-3)] p-6">
                No expenses tracked yet. Add PropStream, InvestorBase, VA salary, dialer, and
                any coaching or education subscriptions to see your true cost per deal.
              </td></tr>
            )}
            {rows.map((r) => {
              const monthly = toMonthly({ ...r, monthly_cost: +r.monthly_cost || 0 });
              return (
                <tr key={r.id} className={`border-b border-[var(--line)] ${r._dirty ? "bg-amber-50" : ""}`}
                    data-testid={`expense-row-${r.id}`}>
                  <td className="p-2">
                    <input className="form-input" value={r.name}
                           onChange={(e) => patch(r.id, "name", e.target.value)} />
                  </td>
                  <td className="p-2">
                    <input className="form-input" value={r.category}
                           onChange={(e) => patch(r.id, "category", e.target.value)}
                           style={{ width: 140 }} />
                  </td>
                  <td className="p-2">
                    <select className="form-input" value={r.billing_frequency}
                            onChange={(e) => patch(r.id, "billing_frequency", e.target.value)}
                            style={{ width: 120 }}>
                      {FREQ.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </td>
                  <td className="p-2">
                    <input type="number" className="form-input" value={r.monthly_cost} min={0} step={1}
                           onChange={(e) => patch(r.id, "monthly_cost", e.target.value)}
                           style={{ width: 100 }} />
                  </td>
                  <td className="p-2 font-semibold">{money(monthly)}</td>
                  <td className="p-2 text-[var(--ink-2)]">{money(monthly * 12)}</td>
                  <td className="p-2 flex gap-1">
                    {r._dirty && (
                      <button type="button" onClick={() => save(r)} disabled={busy}
                              className="btn-outline text-[var(--primary)] p-2" title="Save">
                        <Save size={14} />
                      </button>
                    )}
                    <button type="button" onClick={() => removeRow(r.id)}
                            className="btn-outline text-[var(--warn)] p-2" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-[var(--muted)]">
              <tr className="border-t-2 border-[var(--line)]">
                <td className="p-2 font-bold" colSpan={4}>Total overhead</td>
                <td className="p-2 font-display font-black">{money(totalMonthly)}</td>
                <td className="p-2 font-display font-black text-[var(--ink-2)]">{money(totalAnnual)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <button type="button" onClick={addRow} className="btn-outline" data-testid="expenses-add">
        <Plus size={14} /> Add expense
      </button>
      <button type="button" onClick={() => downloadCsv("expenses")}
              className="btn-outline ml-2" data-testid="expenses-download">
        <Download size={14} /> Download CSV
      </button>
    </div>
  );
}
